/* eslint-disable no-unsafe-optional-chaining */
import i18n from '@_plugins/i18n';
import { container } from '@_plugins/ioc';
import LaravelAPI from '@data/api/laravel.api';
import {
  toRefundRequest,
  toRefundRequestIncluded,
} from '@data/dto/refund-request.dto';
import { RefundRequestRawIncluded } from '@data/dto/refund-request.dto';
import { Meta, PaginateResult } from '@data/models/api.interface';
import { FilterParams } from '@data/models/filter.interface';
import {
  RefundRequestHttp,
  RefundRequestWizard,
} from '@data/models/refund-request.interface';
import { RefundRequest } from '@data/models/refund-request.interface';
import ErrorService from 'core/error.service';
import { injectable } from 'inversify';

@injectable()
export default class RefundRequestService {
  private API = container.get<LaravelAPI>(LaravelAPI);
  private readonly refundRequestEndpoint = '/refund-requests';
  private errorService = container.get<ErrorService>(ErrorService);

  public async listRefundRequests(
    filters: FilterParams,
  ): Promise<PaginateResult<RefundRequest[]>> {
    const { data: response, error } = await this.API?.get<{
      data: RefundRequestHttp[];
      meta: Meta;
      included?: RefundRequestRawIncluded;
    }>(this.refundRequestEndpoint, {
      ...filters,
      include: [
        'training_action_candidate',
        'financing_type_repository',
        'financing_type_repository.parent',
      ],
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return {
        items: [],
        totalSize: 0,
      };
    }

    const included = toRefundRequestIncluded(response?.included);

    const items =
      response?.data?.map((item) => toRefundRequest(item, included)) || [];

    return {
      items,
      totalSize: items.length,
    };
  }

  public async upsertRefundRequest({
    id,
    ...refundRequestWizard
  }: RefundRequestWizard): Promise<RefundRequest | void> {
    if (!id) {
      const { data, error } = await this.API?.post<
        { data: Omit<RefundRequestWizard, 'id'> },
        { data: RefundRequestHttp }
      >(this.refundRequestEndpoint, { data: refundRequestWizard });

      if (error) {
        this.errorService.handleHttpError(error);
        return;
      }
      return toRefundRequest(data?.data || {}, {});
    }

    const { data, error } = await this.API?.patch<
      { data: RefundRequestWizard },
      { data: RefundRequestHttp }
    >(`${this.refundRequestEndpoint}/${id}`, {
      data: { ...refundRequestWizard, id },
    });

    if (error) {
      this.errorService.handleHttpError(error);
      return;
    }
    return toRefundRequest(data?.data || {}, {});
  }

  public async deleteRefundRequest({
    id,
  }: Pick<RefundRequest, 'id'>): Promise<RefundRequest | void> {
    if (!id) {
      this.errorService.handleError({
        title: i18n.t('error.deleting'),
        message: i18n.t('error.id.missing'),
      });
      return;
    }

    const { data, error, status } = await this.API?.delete<{
      data: RefundRequestHttp;
    }>(`${this.refundRequestEndpoint}/${id}`);

    if (status !== 204 && error) {
      this.errorService.handleHttpError(error);
      return;
    }

    return toRefundRequest(data?.data || {}, {});
  }
}
